import {
    Alert,
    Box,
    Button,
    Checkbox,
    Container,
    CopyToClipboard,
    FormField,
    Header,
    Input,
    Modal,
    SpaceBetween,
    Spinner
} from "@amzn/awsui-components-react";
import { KnowledgeBase } from "@amzn/ask-legal-domain";
import * as React from "react";
import { AITeammateAccessOutput } from "../../api/knowledge-base.api";
import { useAPI2 } from "../../hooks/api-hook";
import { AppContext } from "../../setup/context";
import { ErrorFlashbar } from "../common/ErrorFlashbar";

/**
 * Connects the instance's Knowledge Base to AI Teammate. Grants create/extend a per-instance IAM
 * role whose trust policy lists the AI Teammate role ARNs pasted here (one per TeamWorks team) -
 * the role, not the Knowledge Base, is what gets shared, so removing a teammate ARN revokes that
 * team without touching the others. State lives in IAM; there is nothing to keep in sync here.
 */
export const AITeammateAccessSection = (props: {
    instanceId: string;
    knowledgeBaseId: string;
    knowledgeBaseArn: string;
}) => {
    const context = React.useContext(AppContext);
    const api = context.getKnowledgeBaseAPI();
    const getAccessRunner = useAPI2((instanceId: string) => api.getAITeammateAccess(instanceId));
    const connectRunner = useAPI2(
        (i: { instanceId: string; teammateRoleArn: string }) =>
            api.connectAITeammate(i.instanceId, i.teammateRoleArn)
    );
    const disconnectRunner = useAPI2(
        (i: { instanceId: string; teammateRoleArn?: string }) =>
            api.disconnectAITeammate(i.instanceId, i.teammateRoleArn)
    );

    const [access, setAccess] = React.useState<AITeammateAccessOutput>(null);
    const [teammateArn, setTeammateArn] = React.useState("");
    const [acknowledged, setAcknowledged] = React.useState(false);
    // Which disconnect is in flight: a teammate ARN for a single Remove, ALL for disconnect-all.
    // Only the clicked control shows loading; every other disconnect control is disabled so a
    // second, conflicting request cannot start while one is running.
    const DISCONNECT_ALL = "ALL";
    const [disconnectTarget, setDisconnectTarget] = React.useState<string>(null);
    const [showDisconnectAllConfirm, setShowDisconnectAllConfirm] = React.useState(false);
    // Connect and disconnect both read-modify-write the same IAM trust policy server-side, so
    // each side stays disabled while the other is in flight - one mutation at a time.
    const disconnecting = disconnectRunner.status === "Running";
    const connecting = connectRunner.status === "Running";

    React.useEffect(() => {
        getAccessRunner.invoke(props.instanceId);
    }, [props.instanceId]);

    React.useEffect(() => {
        if (getAccessRunner.status === "Succeeded") setAccess(getAccessRunner.output);
    }, [getAccessRunner.status, getAccessRunner.output]);

    React.useEffect(() => {
        if (connectRunner.status !== "Succeeded") return;
        setAccess(connectRunner.output);
        setTeammateArn("");
        setAcknowledged(false);
    }, [connectRunner.status, connectRunner.output]);

    React.useEffect(() => {
        if (disconnectRunner.status === "Succeeded") setAccess(disconnectRunner.output);
    }, [disconnectRunner.status, disconnectRunner.output]);

    // us-east-1 from arn:aws:bedrock:us-east-1:<account>:knowledge-base/<id>
    const region = props.knowledgeBaseArn.split(":")[3];
    // The AI Teammate form's region dropdown shows AWS's friendly names, not region codes, so
    // the admin needs the same label to match against. Fall back to the bare code if unmapped.
    const REGION_DISPLAY_NAMES: Record<string, string> = {
        "us-east-1": "US East (N. Virginia)",
        "us-east-2": "US East (Ohio)",
        "us-west-1": "US West (N. California)",
        "us-west-2": "US West (Oregon)"
    };
    const regionDisplay = REGION_DISPLAY_NAMES[region]
        ? `${REGION_DISPLAY_NAMES[region]} - ${region}`
        : region;
    const trimmedArn = teammateArn.trim();
    const arnIsValid = KnowledgeBase.isValidIamRoleArn(trimmedArn);
    const connected = access?.trustedTeammateArns ?? [];

    /** `display` shows a friendlier label while the copy button still copies the raw value. */
    const copyRow = (label: string, value: string, display?: string) => (
        <SpaceBetween size="xxs">
            <Box variant="awsui-key-label">{label}</Box>
            <SpaceBetween size="xxs" direction="horizontal" alignItems="center">
                {display && <Box>{display}</Box>}
                <CopyToClipboard
                    variant={display ? "icon" : "inline"}
                    textToCopy={value}
                    copyButtonAriaLabel={`Copy ${label}`}
                    copySuccessText={`${label} copied`}
                    copyErrorText={`${label} failed to copy`}
                />
            </SpaceBetween>
        </SpaceBetween>
    );

    return (
        <SpaceBetween size="m">
            {getAccessRunner.status === "Error" && <ErrorFlashbar error={getAccessRunner.err} />}
            {connectRunner.status === "Error" && <ErrorFlashbar error={connectRunner.err} />}
            {disconnectRunner.status === "Error" && <ErrorFlashbar error={disconnectRunner.err} />}
            <Modal
                visible={showDisconnectAllConfirm}
                onDismiss={() => setShowDisconnectAllConfirm(false)}
                header="Disconnect AI Teammate"
                footer={
                    <Box float="right">
                        <SpaceBetween direction="horizontal" size="xs">
                            <Button onClick={() => setShowDisconnectAllConfirm(false)}>Cancel</Button>
                            <Button
                                variant="primary"
                                onClick={() => {
                                    setShowDisconnectAllConfirm(false);
                                    setDisconnectTarget(DISCONNECT_ALL);
                                    disconnectRunner.invoke({ instanceId: props.instanceId });
                                }}
                            >
                                Disconnect all
                            </Button>
                        </SpaceBetween>
                    </Box>
                }
            >
                This removes Knowledge Base access for {connected.length === 1 ?
                    "the connected AI Teammate" : `all ${connected.length} connected AI Teammates`} at
                once. Each team will need to reconnect from TeamWorks to restore access.
            </Modal>
            <Container header={
                <Header
                    variant="h3"
                    description="Let a TeamWorks AI Teammate answer questions from this Knowledge Base. Each team pastes its own AI Teammate role ARN here; access can be removed per team at any time."
                    actions={connected.length > 0 &&
                        <Button
                            loading={disconnecting && disconnectTarget === DISCONNECT_ALL}
                            disabled={connecting || (disconnecting && disconnectTarget !== DISCONNECT_ALL)}
                            onClick={() => setShowDisconnectAllConfirm(true)}
                        >
                            Disconnect AI Teammate
                        </Button>
                    }
                >
                    AI Teammate access
                </Header>}>
                {getAccessRunner.status === "Running" && !access ?
                    <Box textAlign="center" className="awsui-util-status-inactive">
                        <Spinner /> Loading AI Teammate access
                    </Box> :
                    <SpaceBetween size="l">
                        <SpaceBetween size="s">
                            <Box variant="h4">Step 1: Grant access to your AI Teammate</Box>
                            <FormField
                                label="AI Teammate role ARN"
                                description={"In TeamWorks, open Connections > Knowledge > Add knowledge source, choose \"Bedrock Knowledge Base\" as the source type (not Kendra), and copy the AI Teammate ARN shown there."}
                                errorText={teammateArn && !arnIsValid ?
                                    "Enter an IAM role ARN like arn:aws:iam::123456789012:role/AI-Teammate-..." : null}
                            >
                                <Input
                                    value={teammateArn}
                                    placeholder="arn:aws:iam::123456789012:role/AI-Teammate-..."
                                    onChange={({ detail }) => setTeammateArn(detail.value)}
                                />
                            </FormField>
                            <Alert type="warning">
                                Important: once an AI Teammate is granted access to this Knowledge Base,
                                the published content of every opted-in page becomes retrievable through
                                that AI Teammate - regardless of the pages&apos; PathFinder permissions
                                (&quot;Legal Only&quot; and viewer restrictions do not carry over). Who can
                                see it is determined by where that AI Teammate is deployed - for example,
                                every member of a Slack channel it is added to - not by PathFinder. Only
                                connect AI Teammates whose audience may read every opted-in page.
                            </Alert>
                            <Checkbox
                                checked={acknowledged}
                                onChange={({ detail }) => setAcknowledged(detail.checked)}
                            >
                                I understand and accept the exposure above
                            </Checkbox>
                            <Button
                                variant="primary"
                                disabled={!arnIsValid || !acknowledged || disconnecting}
                                loading={connecting}
                                onClick={() => connectRunner.invoke({
                                    instanceId: props.instanceId,
                                    teammateRoleArn: trimmedArn
                                })}
                            >
                                Grant access
                            </Button>
                        </SpaceBetween>
                        {connected.length > 0 && <SpaceBetween size="s">
                            <Box variant="h4">Connected AI Teammates</Box>
                            {connected.map(arn => (
                                <SpaceBetween key={arn} direction="horizontal" size="s" alignItems="center">
                                    <Box fontSize="body-s">{arn}</Box>
                                    <Button
                                        variant="inline-link"
                                        loading={disconnecting && disconnectTarget === arn}
                                        disabled={connecting || (disconnecting && disconnectTarget !== arn)}
                                        onClick={() => {
                                            setDisconnectTarget(arn);
                                            disconnectRunner.invoke({
                                                instanceId: props.instanceId,
                                                teammateRoleArn: arn
                                            });
                                        }}
                                    >
                                        Remove
                                    </Button>
                                </SpaceBetween>
                            ))}
                        </SpaceBetween>}
                        {access?.accessRoleArn && <SpaceBetween size="s">
                            <Box variant="h4">Step 2: Finish in TeamWorks</Box>
                            <Box variant="p" color="text-body-secondary">
                                Back in the Add knowledge source form, with &quot;Bedrock Knowledge
                                Base&quot; selected as the source type, paste these values. AI Teammate
                                validates the connection when you submit.
                            </Box>
                            {copyRow("Knowledge Base ID", props.knowledgeBaseId)}
                            {copyRow("Region", region, regionDisplay)}
                            {copyRow("IAM role ARN", access.accessRoleArn)}
                        </SpaceBetween>}
                    </SpaceBetween>
                }
            </Container>
        </SpaceBetween>
    );
};
